import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { UserRoleProvider } from "@/contexts/UserRoleContext";
import Index from "./pages/Index"; // Eager: landing page (LCP critical)
import CookieBanner from "./components/CookieBanner";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthHashHandler } from "./components/AuthHashHandler";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { MobileInstallBanner } from "./components/MobileInstallBanner";

// Lazy-loaded routes (code-splitting)
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const Register = lazy(() => import("./pages/Register"));
const ActivateTrial = lazy(() => import("./pages/ActivateTrial"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CooperativeDashboard = lazy(() => import("./pages/CooperativeDashboard"));
const ParcelsPage = lazy(() => import("./pages/ParcelsPage"));
const DroughtWaterStressPage = lazy(() => import("./pages/DroughtWaterStressPage"));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage"));
const ProductMarketplace = lazy(() => import("./pages/ProductMarketplace"));
const AlertsPage = lazy(() => import("./pages/AlertsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const InsuranceCompanyDashboard = lazy(() => import("./pages/InsuranceCompanyDashboard"));
const Features = lazy(() => import("./pages/Features"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const SatelliteRouteHandler = lazy(() => import("./pages/SatelliteRouteHandler"));
const AIAnalytics = lazy(() => import("./pages/AIAnalytics"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const AlertsManagement = lazy(() => import("./pages/AlertsManagement"));
const IoTSensorsPage = lazy(() => import("./pages/IoTSensorsPage"));
const GlobalCompliancePage = lazy(() => import("./pages/GlobalCompliancePage"));
const RegulatoryReadiness = lazy(() => import("./pages/RegulatoryReadiness"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancelled = lazy(() => import("./pages/PaymentCancelled"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OlympicBenchmarkAnalysis = lazy(() => import("./pages/OlympicBenchmarkAnalysis"));
const Contact = lazy(() => import("./pages/Contact"));
const LandingV2 = lazy(() => import("./pages/LandingV2"));
const Technology = lazy(() => import("./pages/Technology"));
const AdminLaboratories = lazy(() => import("./pages/AdminLaboratories"));
const AdminTools = lazy(() => import("./pages/AdminTools"));
const ProspectiveFieldSetup = lazy(() => import("./pages/admin/ProspectiveFieldSetup"));
const DataSources = lazy(() => import("./pages/DataSources"));
const StandardsMethodologies = lazy(() => import("./pages/StandardsMethodologies"));
const AdvancedTools = lazy(() => import("./pages/AdvancedTools"));
const Architecture = lazy(() => import("./pages/Architecture"));
const MarketPrices = lazy(() => import("./pages/MarketPrices"));
const IrrigationManager = lazy(() => import("./pages/IrrigationManager"));
const PestDiseaseManagement = lazy(() => import("./pages/PestDiseaseManagement"));
const MarketPriceTrends = lazy(() => import("./pages/MarketPriceTrends"));
const MarketPriceRegional = lazy(() => import("./pages/MarketPriceRegional"));
const MarketAnalytics = lazy(() => import("./pages/MarketAnalytics"));
const TraceabilityPage = lazy(() => import("./pages/TraceabilityPage"));
// Reports (basic) page removed — redirects to unified Analytics & Reports
const UrbanDashboard = lazy(() => import("./pages/UrbanDashboard"));
const UrbanFertilization = lazy(() => import("./pages/UrbanFertilization"));
const UrbanIrrigation = lazy(() => import("./pages/UrbanIrrigation"));
const FieldNotebookPage = lazy(() => import("./pages/FieldNotebookPage"));
const FieldNotebookIndex = lazy(() => import("./pages/FieldNotebookIndex"));
const Addons = lazy(() => import("./pages/Addons"));
const SolutionsIndex = lazy(() => import("./pages/solutions/Index"));
const CropInsurance = lazy(() => import("./pages/solutions/CropInsurance"));
const Cooperatives = lazy(() => import("./pages/solutions/Cooperatives"));
const LargeScaleProducers = lazy(() => import("./pages/solutions/LargeScaleProducers"));
const Agribusiness = lazy(() => import("./pages/solutions/Agribusiness"));
const FarmsCooperatives = lazy(() => import("./pages/solutions/FarmsCooperatives"));
const AgriculturalInsurers = lazy(() => import("./pages/solutions/AgriculturalInsurers"));
const AgronomicAdvisors = lazy(() => import("./pages/solutions/AgronomicAdvisors"));
const Platform = lazy(() => import("./pages/Platform"));
const ResourcesDataSources = lazy(() => import("./pages/resources/DataSources"));
const ResourcesSampleReport = lazy(() => import("./pages/resources/SampleReport"));
const ResourcesPartners = lazy(() => import("./pages/resources/PartnersPage"));
const Students = lazy(() => import("./pages/Students"));
const PUMonitoring = lazy(() => import("./pages/PUMonitoring"));
const DeploymentReadiness = lazy(() => import("./pages/DeploymentReadiness"));
const PilotUserManagement = lazy(() => import("./pages/admin/PilotUserManagement"));
const MissionControl = lazy(() => import("./pages/MissionControl"));
const RiskManagement = lazy(() => import("./pages/RiskManagement"));
const RiskAnalysisML = lazy(() => import("./pages/RiskAnalysisML"));
const SatelliteImages = lazy(() => import("./pages/SatelliteImages"));
const IrrigationReports = lazy(() => import("./pages/IrrigationReports"));
const EFFISFireTest = lazy(() => import("./pages/EFFISFireTest"));
const Fertilization = lazy(() => import("./pages/Fertilization"));
const PKOptimizerPage = lazy(() => import("./pages/PKOptimizerPage"));
const NitrogenOptimizerPage = lazy(() => import("./pages/NitrogenOptimizerPage"));
const FrostProtection = lazy(() => import("./pages/FrostProtection"));
const ParcelPassportPage = lazy(() => import("./pages/ParcelPassportPage"));
const VerifyCertificatePage = lazy(() => import("./pages/VerifyCertificatePage"));
const FoliarAnalysis = lazy(() => import("./pages/FoliarAnalysis"));
const PhenologyTracker = lazy(() => import("./pages/PhenologyTracker"));
const AnalyticsUnified = lazy(() => import("./pages/AnalyticsUnified"));
const GroundTruthComparison = lazy(() => import("./pages/GroundTruthComparison"));
const SupportTickets = lazy(() => import("./pages/SupportTickets"));
const MethodologyET0 = lazy(() => import("./pages/MethodologyET0"));
const ISO11783Certification = lazy(() => import("./pages/ISO11783Certification"));
const FAO56Methodology = lazy(() => import("./pages/FAO56Methodology"));
const VerifyISO11783 = lazy(() => import("./pages/VerifyISO11783"));
const VerifyFAO56 = lazy(() => import("./pages/VerifyFAO56"));
const VerifyDualKc = lazy(() => import("./pages/VerifyDualKc"));
const VerifyReport = lazy(() => import("./pages/VerifyReport"));
const CropResources = lazy(() => import("./pages/CropResources"));
const DownloadISO11783 = lazy(() => import("./pages/DownloadISO11783"));
const DownloadFAO56 = lazy(() => import("./pages/DownloadFAO56"));
const PrivacySettings = lazy(() => import("./pages/PrivacySettings"));
const ValidationDashboard = lazy(() => import("./pages/ValidationDashboard"));
const ScientificTransparency = lazy(() => import("./pages/ScientificTransparency"));
const UserManual = lazy(() => import("./pages/UserManual"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const VerifiedFarm = lazy(() => import("./pages/VerifiedFarm"));
const FireDetection = lazy(() => import("./pages/FireDetection"));
const FirePropagationDemo = lazy(() => import("./components/fire/FirePropagationDemo").then(m => ({ default: m.FirePropagationDemo })));
const DashboardLayout = lazy(() => import("./components/DashboardLayout"));
const AdvancedVegetationAnalysis = lazy(() => import("./pages/AdvancedVegetationAnalysis"));
const SplitViewPage = lazy(() => import("./pages/SplitViewPage"));
const CopernicusDataPage = lazy(() => import("./pages/CopernicusDataPage"));
const FloodRiskPage = lazy(() => import("./pages/FloodRiskPage"));
const Install = lazy(() => import("./pages/Install"));
const IntelligentFarmingPage = lazy(() => import("./pages/IntelligentFarmingPage"));
const SeasonsPage = lazy(() => import("./pages/SeasonsPage"));
const NDVIZonesPage = lazy(() => import("./pages/NDVIZonesPage"));
const DiseaseRiskPage = lazy(() => import("./pages/DiseaseRiskPage"));
const MachineryImportPage = lazy(() => import("./pages/MachineryImportPage"));
const GolfCourseDashboard = lazy(() => import("./pages/GolfCourseDashboard"));
const AgBusinessDashboard = lazy(() => import("./pages/AgBusinessDashboard"));
const MultiFarmView = lazy(() => import("./pages/MultiFarmView"));
const VRAShapefileBuilder = lazy(() => import("./pages/VRAShapefileBuilder"));
const CropClassificationPage = lazy(() => import("./pages/CropClassificationPage"));
const AcademicGamification = lazy(() => import("./pages/AcademicGamification"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const ICP = lazy(() => import("./pages/ICP"));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Disable refetch on window focus to prevent data loss when switching tabs
      refetchOnWindowFocus: false,
      // Keep data fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
    },
  },
});

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <UserRoleProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthHashHandler />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/" element={<LandingV2 />} />
          <Route path="/landing-v2" element={<Navigate to="/" replace />} />
          <Route path="/legacy-home" element={<Index />} />
          {/* Blog routes removed — content consolidated under /features and /technology */}
          <Route path="/blog" element={<Navigate to="/features" replace />} />
          <Route path="/blog/*" element={<Navigate to="/features" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/activate-trial" element={<ActivateTrial />} />
          <Route path="/install" element={<Install />} />
          <Route path="/parcels" element={<ParcelsPage />} />
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager']}>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/cooperative" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <CooperativeDashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/parcels" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager']}>
              <ParcelsPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/parcels/:parcelId" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager']}>
              <ParcelsPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/drought-water-stress" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager']}>
              <DroughtWaterStressPage />
            </ProtectedRoute>
          } />
          {/* NEW: Unified Analytics with tabs */}
          <Route path="/dashboard/analytics-unified" element={
            <ProtectedRoute allowedRoles={['farmer', 'urban_manager', 'admin', 'insurer']}>
              <AnalyticsUnified />
            </ProtectedRoute>
          } />
          
          {/* REDIRECTS: Maintain backward compatibility */}
          <Route path="/dashboard/analytics" element={<Navigate to="/dashboard/analytics-unified?tab=basic" replace />} />
          <Route path="/dashboard/ai-analytics" element={<Navigate to="/dashboard/analytics-unified?tab=ai" replace />} />
          <Route path="/dashboard/reports-center" element={<Navigate to="/dashboard/analytics-unified?tab=reports" replace />} />
          
          {/* Legacy routes redirect to unified analytics */}
          <Route path="/analytics-enhanced" element={<Navigate to="/dashboard/analytics-unified?tab=basic" replace />} />
          <Route path="/analytics-advanced" element={<Navigate to="/dashboard/analytics-unified?tab=ai" replace />} />
          <Route path="/ai-analytics" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <AIAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/market-prices" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <MarketPrices />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/market-prices/trends" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <MarketPriceTrends />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/market-prices/regional" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <MarketPriceRegional />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/market-prices/analytics" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <MarketAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/alerts-management" element={<AlertsManagement />} />
          <Route path="/dashboard/alerts" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager', 'insurer']}>
              <AlertsPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/calendar" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager']}>
              <CalendarPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/settings" element={<SettingsPage />} />
          <Route path="/dashboard/privacy" element={
            <ProtectedRoute allowedRoles={['farmer', 'insurer', 'admin', 'urban_manager']}>
              <PrivacySettings />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/advanced-tools" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager']}>
              <AdvancedTools />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/vegetation-analysis" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager']}>
              <AdvancedVegetationAnalysis />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/ndvi-zones" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager']}>
              <NDVIZonesPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/advanced-analytics" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <SplitViewPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/admin-tools" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminTools />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/admin/prospective-field" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ProspectiveFieldSetup />
            </ProtectedRoute>
          } />
          <Route path="/data-sources" element={<DataSources />} />
          <Route path="/standards-methodologies" element={<StandardsMethodologies />} />
          <Route path="/dashboard/satellite" element={
            <ProtectedRoute allowedRoles={['farmer', 'urban_manager']}>
              <SatelliteRouteHandler />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/satellite-images" element={
            <ProtectedRoute allowedRoles={['farmer', 'urban_manager', 'admin']}>
              <SatelliteImages />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/fertilization" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <Fertilization />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/pk-optimizer" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <PKOptimizerPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/fertilizer/nitrogen" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <NitrogenOptimizerPage />
            </ProtectedRoute>
          } />
          {/* Duplicate /dashboard/ai-analytics removed — redirect at line 214 handles this */}
          <Route path="/dashboard/irrigation" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <IrrigationManager />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/irrigation-reports" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <IrrigationReports />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/risk-analysis-ml" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <RiskAnalysisML />
            </ProtectedRoute>
          } />
          <Route path="/test/effis-fire" element={
            <ProtectedRoute allowedRoles={['admin', 'farmer']}>
              <EFFISFireTest />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/frost-protection" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <FrostProtection />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/fire-detection" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager']}>
              <FireDetection />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/fire-propagation" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <DashboardLayout>
                <FirePropagationDemo />
              </DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/foliar-analysis" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <FoliarAnalysis />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/phenology" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <PhenologyTracker />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/crop-resources" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <CropResources />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/ground-truth" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <GroundTruthComparison />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/academic-gamification" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <AcademicGamification />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/validation" element={
            <ProtectedRoute allowedRoles={['farmer', 'urban_manager', 'admin']}>
              <ValidationDashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/copernicus" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <CopernicusDataPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/flood-risk" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <FloodRiskPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/intelligent-farming" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <IntelligentFarmingPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/seasons" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <SeasonsPage />
            </ProtectedRoute>
          } />
          <Route path="/scientific-transparency" element={
            <ScientificTransparency />
          } />
          <Route path="/methodology-et0" element={
            <ProtectedRoute allowedRoles={['farmer', 'urban_manager', 'admin']}>
              <MethodologyET0 />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/support" element={
            <ProtectedRoute allowedRoles={['farmer', 'urban_manager', 'insurer', 'admin']}>
              <SupportTickets />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/pest-diseases" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <PestDiseaseManagement />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/disease-risk-map" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <DiseaseRiskPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/machinery-import" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <MachineryImportPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/agbusiness" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <AgBusinessDashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/multi-farm" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <MultiFarmView />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/vra-shapefile" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <VRAShapefileBuilder />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/crop-classification" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <CropClassificationPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/integrations" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <IntegrationsPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/icp" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager', 'insurer']}>
              <ICP />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/golf-course" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager']}>
              <GolfCourseDashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/insurance" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard/insurance-company" element={
            <ProtectedRoute allowedRoles={['insurer', 'admin']}>
              <InsuranceCompanyDashboard />
            </ProtectedRoute>
          } />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/dashboard/marketplace" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <MarketplacePage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/product-marketplace" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <ProductMarketplace />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/parametric-insurance" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard/certified-marketplace" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard/prefinancing" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard/credit-scoring" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard/traceability" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <TraceabilityPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/verified-farm" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <VerifiedFarm />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/parcel-passport" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin']}>
              <ParcelPassportPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/reports" element={<Navigate to="/dashboard/analytics-unified?tab=reports" replace />} />
          <Route path="/dashboard/iot" element={
            <ProtectedRoute allowedRoles={['farmer', 'urban_manager']}>
              <IoTSensorsPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/field-notebook" element={
            <ProtectedRoute allowedRoles={['farmer', 'urban_manager', 'admin']}>
              <FieldNotebookIndex />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/field-notebook/:parcelId" element={
            <ProtectedRoute allowedRoles={['farmer', 'urban_manager', 'admin']}>
              <FieldNotebookPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/pu-monitoring" element={
            <ProtectedRoute allowedRoles={['farmer', 'admin', 'urban_manager']}>
              <PUMonitoring />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/mission-control" element={
            <ProtectedRoute allowedRoles={['farmer', 'urban_manager', 'admin']}>
              <MissionControl />
            </ProtectedRoute>
          } />
          {/* Removed: now redirects to analytics-unified?tab=reports */}
          <Route path="/dashboard/risk-management" element={
            <ProtectedRoute allowedRoles={['insurer', 'admin']}>
              <RiskManagement />
            </ProtectedRoute>
          } />
          <Route path="/web3-insurance" element={<Navigate to="/" replace />} />
          <Route path="/web3-insurance-advanced" element={<Navigate to="/" replace />} />
          <Route path="/web3-smart-contracts" element={<Navigate to="/" replace />} />
          <Route path="/smart-contracts" element={<Navigate to="/" replace />} />
          <Route path="/ai-analytics" element={<AIAnalytics />} />
          <Route path="/mobile-app" element={<Navigate to="/" replace />} />
          <Route path="/ux-revolution" element={<Navigate to="/" replace />} />
          <Route path="/presentation" element={<Navigate to="/" replace />} />
          <Route path="/insurance-guide" element={<Navigate to="/" replace />} />
          <Route path="/iot-sensors" element={<IoTSensorsPage />} />
          <Route path="/defi-marketplace" element={<Navigate to="/" replace />} />
          <Route path="/global-compliance" element={<GlobalCompliancePage />} />
          <Route path="/regulatory-readiness" element={<RegulatoryReadiness />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
          <Route path="/insurance" element={<Navigate to="/" replace />} />
          <Route path="/help-center" element={<HelpCenter />} />
          <Route path="/help" element={<Navigate to="/help-center" replace />} />
          <Route path="/dashboard/user-manual" element={
            <ProtectedRoute allowedRoles={['farmer', 'urban_manager', 'admin', 'insurer']}>
              <UserManual />
            </ProtectedRoute>
          } />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />
           <Route path="/olympic-benchmark/:parcelId" element={<OlympicBenchmarkAnalysis />} />
           <Route path="/contact" element={<Contact />} />
            <Route path="/technology" element={<Technology />} />
            <Route path="/architecture" element={<Architecture />} />
            <Route path="/risk-assessment" element={<Navigate to="/" replace />} />
            <Route path="/claims" element={<Navigate to="/" replace />} />
            <Route path="/deployment-readiness" element={<ProtectedRoute><DeploymentReadiness /></ProtectedRoute>} />
            <Route path="/system-status" element={<ProtectedRoute><DeploymentReadiness /></ProtectedRoute>} />
            <Route path="/admin/pilot-users" element={<ProtectedRoute><PilotUserManagement /></ProtectedRoute>} />
            <Route path="/admin/laboratories" element={<ProtectedRoute><AdminLaboratories /></ProtectedRoute>} />
           
          {/* Urban Manager Dashboard - Single entry point, all other features accessible via main routes */}
          <Route 
            path="/dashboard/urban" 
            element={
              <ProtectedRoute allowedRoles={['urban_manager']}>
                <UrbanDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/urban-fertilization" 
            element={
              <ProtectedRoute allowedRoles={['urban_manager']}>
                <UrbanFertilization />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/urban-irrigation" 
            element={
              <ProtectedRoute allowedRoles={['urban_manager']}>
                <UrbanIrrigation />
              </ProtectedRoute>
            } 
          />
          
          {/* Add-ons & Solutions Pages */}
          <Route path="/addons" element={<Addons />} />
          <Route path="/solutions" element={<SolutionsIndex />} />
          <Route path="/solutions/crop-insurance" element={<CropInsurance />} />
          <Route path="/solutions/cooperatives" element={<Cooperatives />} />
          <Route path="/solutions/large-scale-producers" element={<LargeScaleProducers />} />
          <Route path="/solutions/agribusiness" element={<Agribusiness />} />
          <Route path="/solutions/farms-cooperatives" element={<FarmsCooperatives />} />
          <Route path="/solutions/agricultural-insurers" element={<AgriculturalInsurers />} />
          <Route path="/solutions/agronomic-advisors" element={<AgronomicAdvisors />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/resources/data-sources" element={<ResourcesDataSources />} />
          <Route path="/resources/sample-report" element={<ResourcesSampleReport />} />
          <Route path="/resources/partners" element={<ResourcesPartners />} />
          
          {/* Student Plan */}
          <Route path="/students" element={<Students />} />
          
          {/* Certifications */}
          <Route path="/certification/iso11783" element={<ISO11783Certification />} />
          <Route path="/certification/fao56" element={<FAO56Methodology />} />
          
          {/* Verification Pages */}
          <Route path="/verify/iso11783" element={<VerifyISO11783 />} />
          <Route path="/verify/fao56" element={<VerifyFAO56 />} />
          <Route path="/verify/dual-kc" element={<VerifyDualKc />} />
          <Route path="/verify/certificate" element={<VerifyCertificatePage />} />
          <Route path="/verify/report" element={<VerifyReport />} />
          
          {/* Direct Download Pages */}
          <Route path="/docs/iso11783" element={<DownloadISO11783 />} />
          <Route path="/docs/fao56" element={<DownloadFAO56 />} />
          
          {/* Deployment & System Status */}
          <Route path="/deployment-readiness" element={<DeploymentReadiness />} />
          <Route path="/system-status" element={<DeploymentReadiness />} />
           
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
          </Suspense>
        <PWAInstallPrompt />
        <MobileInstallBanner />
        <CookieBanner />
      </BrowserRouter>
    </TooltipProvider>
    </UserRoleProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;

